import React, { useMemo, useState } from 'react';
import { Folder, LayoutGrid, List, MoreHorizontal, Network, Plus } from 'lucide-react';
import { TRACKS, GROUP_NAMES, TrackStatus } from '../../data/workspaceTracks';
import { EmptyState, Glyph, Panel, PrimaryAction, Row, RowText, ScreenHeader, SearchField, Segmented, SelectPill, StatusRing, SURFACE } from './ui';

// ── Tracks ───────────────────────────────────────────────────────────────────
// A flat, filterable library of tracks. Each row shows the track's name and the
// group that owns it, plus its type. Filters: group, status, type, sort, layout.
//
// Rebuilt on the shared app primitives in ./ui.tsx — see that file for why the
// grid of individually bordered grey boxes became one warm panel of
// hairline-divided rows. No copy or data changed.
// ─────────────────────────────────────────────────────────────────────────────

const STATUSES: {
  value: TrackStatus;
  label: string;
}[] = [{
  value: 'active',
  label: 'Active'
}, {
  value: 'dormant',
  label: 'Dormant'
}, {
  value: 'archived',
  label: 'Archived'
}];
const TYPES = ['All track types', 'area', 'project', 'exploration', 'product / skills backlog'];
const LAYOUTS = [['list', List], ['grid', LayoutGrid], ['tree', Network]] as const;
export function TracksView() {
  const [group, setGroup] = useState('Group');
  const [status, setStatus] = useState<TrackStatus>('active');
  const [type, setType] = useState(TYPES[0]);
  const [sort, setSort] = useState<'Name' | 'Group'>('Name');
  const [layout, setLayout] = useState<'list' | 'grid' | 'tree'>('grid');
  const [query, setQuery] = useState('');
  const visible = useMemo(() => {
    const rows = TRACKS.filter(t => t.status === status).filter(t => group === 'Group' || t.group === group).filter(t => type === TYPES[0] || t.type === type).filter(t => t.name.toLowerCase().includes(query.trim().toLowerCase()));
    return [...rows].sort((a, b) => sort === 'Name' ? a.name.localeCompare(b.name) : a.group.localeCompare(b.group) || a.name.localeCompare(b.name));
  }, [group, status, type, sort, query]);

  // The row's contents are identical in both layouts; only the container
  // differs — a joined list, or separate cards in a grid.
  const rows = visible.map(track => <>
      <Glyph icon={Folder} data-id="element-2891" data-mp="1d04wj1y3h7u3" />
      <RowText title={track.name} meta={`${track.group} · ${track.type}`} data-id="element-2892" data-mp="180s1htx2kugf" />
      <StatusRing status={track.status} data-id="element-2893" data-mp="182v0h3g79xxa" />
      <button type="button" aria-label={`Options for ${track.name}`} className="flex-none rounded-full p-1 text-[#C4B4A2] opacity-0 transition-[color,opacity] duration-200 ease-out hover:text-[#2B211A] focus-visible:opacity-100 group-hover:opacity-100" data-id="element-2894" data-mp="24rkfmm0g0cfk">
        <MoreHorizontal size={16} data-id="element-2895" data-mp="1vwml5ahncrm4" />
      </button>
    </>);
  // No padding of its own: the dashboard's content surface already provides it,
  // and both together were giving the screen a 64px gutter.
  return <div data-id="element-2896" data-mp="2oh4otxkfzmo4">
      <ScreenHeader title="Tracks" action={<PrimaryAction label="New track" data-id="element-2898" data-mp="2jo1pt9wwbl0j">
            <Plus size={15} data-id="element-2899" data-mp="2jm8irtideys0" />
            New track
          </PrimaryAction>} data-id="element-2897" data-mp="2oguu5wogcrc3" />

      {/* Controls on one line under the title, so the heading is not competing
       with five pills for the top of the screen. */}
      <div className="mt-6 flex flex-wrap items-center gap-2.5" data-id="element-2900" data-mp="2w0dx1nh7pfbl">
        <SelectPill value={group} onChange={setGroup} options={['Group', ...GROUP_NAMES]} label="Filter by group" data-id="element-2901" data-mp="30w2pl6utdvye" />
        <SelectPill value={STATUSES.find(s => s.value === status)!.label} onChange={value => setStatus(STATUSES.find(s => s.label === value)!.value)} options={STATUSES.map(s => s.label)} label="Filter by status" data-id="element-2902" data-mp="350k52tu55n3d" />
        <SelectPill value={type} onChange={setType} options={TYPES} label="Filter by track type" data-id="element-2903" data-mp="3ev2f0gq5ievm" />
        <SelectPill value={sort} onChange={value => setSort(value as 'Name' | 'Group')} options={['Name', 'Group']} label="Sort by" prefix="Sort" data-id="element-2904" data-mp="3jqhqj20oryov" />
        <span className="grow" data-id="element-2905" data-mp="3spv1jz4gkvbp" />
        <Segmented value={layout} onChange={setLayout} options={LAYOUTS} data-id="element-2906" data-mp="3sqmvz6mbkn0e" />
      </div>

      <div className="mt-4" data-id="element-2907" data-mp="1j1ysqu0oo52">
        <SearchField value={query} onChange={setQuery} placeholder={`Search ${TRACKS.length} tracks`} label="Search tracks" data-id="element-2908" data-mp="1g6030achvfk" />
      </div>

      {visible.length === 0 ? <div className="mt-5" data-id="element-2909" data-mp="3fysyqxdquneu">
          <EmptyState data-id="element-2910" data-mp="3fxrpmygagazr">No tracks match these filters.</EmptyState>
        </div> : layout === 'grid' ? <ul className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3" data-id="element-2911" data-mp="3tt3i0z6z1pdi">
          {visible.map((track, index) => <li key={track.name} className={`group flex items-center gap-3.5 px-4 py-3.5 transition-[border-color,box-shadow] duration-200 ease-out hover:border-[#DFD3C2] hover:shadow-[0_1px_2px_rgba(43,33,26,0.05),0_10px_24px_-14px_rgba(43,33,26,0.16)] ${SURFACE}`} data-id="element-2912" data-mp="312brah8odsxk" data-mpi={index}>
              {rows[index]}
            </li>)}
        </ul> : <Panel className="mt-5" data-id="element-2913" data-mp="360vfkoaqrgt3">
          {visible.map((track, index) => <Row key={track.name} data-id="element-2914" data-mp="363hn4nxmtwbb" data-mpi={index}>{rows[index]}</Row>)}
        </Panel>}
    </div>;
}